import qs from 'qs';
import axios from 'axios';
import { StatusCodes as HTTP } from 'http-status-codes';
import store from '@/store';
import toCamelCase from '@/helpers/toCamelCase';
import toSnakeCase from '@/helpers/toSnakeCase';
import { getSavedState } from '@/helpers/storageOperations';
import { suppressToasterErrors } from '@/helpers/toasterSuppression';
import { TooComplexError, isTooComplexResponse } from '@/helpers/tooComplexError';
import { RepliesLimitError, isRepliesLimitResponse, stampRepliesLimitScope } from '@/helpers/repliesLimitError';
import { baseApiUrl, apiUrl, publicPath, projectName, isApiMocked } from '@/config';
// import router from "@/router";

// @TMP (--- FOR MOCKED API AND REAL API ---)
// @NOTE routes added here will have request's [response] parsed into camelCase
const parseableResponseRoutes = [
    '/v1/teams',
    '/v1/users',
    '/v1/labels',
    '/v1/filters',
    '/v1/session',
    '/v1/streams',
    '/v1/accounts',
    '/v1/dashboard',
    '/v1/discord',
    '/v1/feed-items',
    '/v1/annotations',
    '/v1/admin/rules',
    '/v1/preferences',
    '/v1/admin/emails',
    '/v1/account_events',
    '/v1/accounts/preferences/security/password_reset',
    '/v1/suggested_keywords',
    '/v1/account/preferences',
    '/v1/team_member_invites',
    '/v1/admin/business_profiles',
    '/v1/admin/accounts/takeover',
    '/v1/helper',
    //@TMP - change after backend standarise routes
    `${baseApiUrl}/accounts/token`,
    `${baseApiUrl}/accounts/logout`,
    '/v1/two_factor_authentications/new'
];

// @TMP (--- FOR MOCKED API ONLY ---)
// @NOTE routes added here will have request's [body/params] parsed into snake_case
const parseableRequestRoutes = ['/v1/feed-items'];

const bannedRoutes = ['v1/moderation'];

axios.defaults.baseURL = apiUrl;
axios.defaults.withCredentials = true;

const pendingRequestControllers = new Set();

let isRedirectingToLoginPage = false;

const untrackRequest = config => {
    const controller = config?.abortController;

    if (controller) {
        pendingRequestControllers.delete(controller);
    }
};

axios.cancelAllRequests = () => {
    pendingRequestControllers.forEach(controller => controller.abort());
    pendingRequestControllers.clear();
};

// keeps a request pending while the browser navigates away
const neverSettlingRequest = () => new Promise(() => {});

const isRouteParseable = (route, parseableRoutes) =>
    parseableRoutes.some(parseableRoute => route?.includes(parseableRoute));

const parseResponse = response => {
    if (
        response?.data &&
        isRouteParseable(response?.config?.url, parseableResponseRoutes) // @TMP
    ) {
        response.data = toCamelCase(response.data);
    }

    return response;
};

axios.interceptors.response.use(
    response => {
        untrackRequest(response.config);

        const parsedResponse = parseResponse(response);

        if (isTooComplexResponse(parsedResponse.data)) {
            throw new TooComplexError(parsedResponse.data.description, parsedResponse);
        }

        if (isRepliesLimitResponse(parsedResponse.data)) {
            throw new RepliesLimitError(parsedResponse.data.description, parsedResponse);
        }

        return parsedResponse;
    },
    async error => {
        untrackRequest(error.config);

        if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
            throw error;
        }

        console.log(error)

        if (!error.response) {
            throw error;
        }

        error.response = parseResponse(error.response);

        const { responseURL } = error.response.request;
        const fromUrl = responseURL.split('?')[0].split('/').pop();
        const nonRedirectUrls = ['password_reset', 'session', 'token'];

        const { status } = error.response;

        error.message = error.response.data?.error_message ||  error.response.data?.description || error.message;

        console.warn('Error status', status);

        if (nonRedirectUrls.includes(fromUrl)) {
            throw error;
        }

        const redirectToLoginPage = () => {
            if (isRedirectingToLoginPage) {
                return;
            }

            isRedirectingToLoginPage = true;
            suppressToasterErrors();

            let { pathname, search } = window.location;
            if (publicPath !== '/' && pathname.startsWith(publicPath)) {
                pathname = pathname.replace(publicPath, '/');
            }
            window.location.href = `${publicPath}login?redirectFrom=${pathname}${search}`;
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith(projectName)) {
                    localStorage.removeItem(key);
                }
            });
        };

        const promoteRequest = (error) => {
            const { config } = error;
            delete config.headers.Authorization;
            config.isRetriedWithNewToken = true;
            return axios.request(config);
        };

        if (status === HTTP.UNAUTHORIZED) {
            if (!error.config?.isRetriedWithNewToken && !isRedirectingToLoginPage) {
                const currentUserId = store.getters['auth/loggedUser']?.id;
                try {
                    await store.dispatch('auth/refreshToken');
                    return promoteRequest(error);
                } catch {
                    try {
                        await store.dispatch('auth/takeToken');
                        const newUserId = store.getters['auth/loggedUser']?.id;
                        if (currentUserId && currentUserId !== newUserId) {
                            window.location = window.location.origin + window.location.pathname;
                            return neverSettlingRequest();
                        } else {
                            return promoteRequest(error);
                        }
                    } catch {
                        console.warn('We were not able to generate a new token.');
                    }
                }
            }

            redirectToLoginPage();

            return neverSettlingRequest();
        }

        throw error;
    }
);

axios.interceptors.request.use(
    request => {
        const requestUrl = request?.url;
        const cookies = getSavedState('cookies');
        const csrfToken = getSavedState('auth.csrfToken');
        const accessToken = getSavedState('auth.accessToken');

        if (csrfToken) {
            request.headers['X-CSRF-TOKEN'] = csrfToken;
        }
        if (accessToken && !request.headers.Authorization) {
            request.headers.Authorization = `Bearer ${accessToken}`;
        }

        const searchParams = new URLSearchParams(window.location.search);
        const sharedLinkCode = searchParams.get('shared_link_code');
        if (sharedLinkCode) {
            request.headers['Shared-Link-Code'] = sharedLinkCode;
        }

        const { params, data, encodeURI } = request;

        const isParseableInMockedApi = isApiMocked && isRouteParseable(requestUrl, parseableRequestRoutes);

        const isBanned = request.params && bannedRoutes.some(route => requestUrl.includes(route));

        if ((!isApiMocked && !isBanned) || isParseableInMockedApi) {
            request.params = toSnakeCase(params);
        }

        if ((!isApiMocked || isParseableInMockedApi) && data && !data.ignoreSnakeCaseFormatting)  {
            request.data = toSnakeCase(data);
        }

        if (cookies?.accountId && params?.accountId) {
            request.params.account_id = cookies.accountId;
        }

        if (encodeURI) {
            request.paramsSerializer = params => {
                return qs.stringify(params, { arrayFormat: 'brackets' });
            };
        }

        if (request.abortController) {
            if (!request.signal?.aborted) {
                pendingRequestControllers.add(request.abortController);
            }
        } else if (!request.signal) {
            const controller = new AbortController();

            request.signal = controller.signal;
            request.abortController = controller;
            pendingRequestControllers.add(controller);
        }

        stampRepliesLimitScope(request);

        return request;
    },
    error => error
);

for (const method of ['request', 'delete', 'get', 'head', 'options', 'post', 'put', 'patch']) {
    axios['$' + method] = function () {
        return this[method].apply(this, arguments).then(res => res && res.data);
    };
}


//@TMP - for overriding post method to get. Sometimes we got long query (especially in compare mode) and as result we got 414 error. BE in future should fix it, but for now we can use this workaround
axios.getMethodOverridePost = function(...args) {
    overrideArgs(args);

    return this.$post(...args);
};

function overrideArgs(args) {
    if (!args[2]) {
        args[2] = {};
    }

    if (!args[2].headers) {
        args[2].headers = {};
    }

    args[2].headers['X-HTTP-Method-Override'] = 'GET';

    if (args[1] && args[1].params) {
        args[1] = { ...args[1].params };
        delete args[1].params;
    }
}

axios.getMethodOverridePostWithHeaders = function(...args) {
    overrideArgs(args);

    return this.post(...args);
};

export default axios;

